import {
  CommentOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Col,
  Dropdown,
  Form,
  Input,
  Menu,
  Modal,
  notification,
  Popover,
  Row,
  Space,
  Typography
} from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import {
  clearNoteAction,
  clearNoteLinkAction,
  createNoteAction,
  deleteNoteAction,
  deleteNoteLinkAction,
  FullOrganizationUnitResponseInterfaceSingle,
  getAllNoteLinksAction,
  getAllNotesAction,
  getSingleNoteAction,
  i18n,
  LicenceInterface,
  LicenceResponseInterfaceAll,
  LicenceService,
  NavigationService,
  NoteInterface,
  NoteLinkInterface,
  NoteLinkModal,
  NoteLinkResponseInterfaceSingle,
  NoteLinkService,
  NoteStateInterface,
  OrganizationUnitService,
  StoreStateInterface,
  updateNoteAction,
  UserActivityLogService,
  UserRoleEnum
} from '../../../../../common';
import { LicenceEnum } from '../../../../../common/enums/licence.enum';
import printService from '../../../../../common/services/print.service';
import { AppRoutes } from '../../../_router/app.routes';
import { UserActionTypeEnum } from '../../../../../common/enums/userActionType.enum';

const { confirm } = Modal;

function NotesCrudPageComponent() {
  const { id } = useParams<{ id: string }>();
  const userAuth = useSelector((state: StoreStateInterface) => state.auth);

  const isNew = isNaN(parseInt(id));

  const dispatch = useDispatch();
  const noteData: NoteStateInterface = useSelector((state: StoreStateInterface) => state.notes);

  const { Text } = Typography;
  const { TextArea } = Input;

  const [orgUnitInfo, setOrgUnitInfo] = useState<FullOrganizationUnitResponseInterfaceSingle>();
  const [licenceData, setLicenceData] = useState<LicenceInterface>();

  const noteLinkData = useSelector((state: StoreStateInterface) => state.notesLinks);

  const [visible, setVisible] = useState(false);
  const [isNewLink, setisNewLink] = useState(false);

  const [selectedLinkId, setSelectedLinkId] = useState(0);
  const [selectedLink, setSelectedLink] = useState<NoteLinkResponseInterfaceSingle>();

  const showModal = () => {
    setVisible(true);
  };

  const handleOk = () => {
    setVisible(false);
    dispatch(clearNoteLinkAction());
  };

  const handleCancel = () => {
    setVisible(false);
    dispatch(clearNoteLinkAction());
  };

  const handleChange = (value: string) => {};

  const typeId = noteData.currentNote?.attributes?.typeId;
  const type = noteData.currentNote?.attributes?.type;

  useEffect(() => {
    dispatch(clearNoteAction());
    dispatch(getAllNoteLinksAction(parseInt(id)));
    if (!isNew) dispatch(getSingleNoteAction(parseInt(id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => {
    clearNoteAction();
    dispatch(getAllNotesAction(userAuth.user?.id));
  };

  useEffect(() => {
    if (userAuth.user?.organizationUnit?.id) {
      OrganizationUnitService.getSingle(userAuth.user?.organizationUnit?.id).subscribe(
        (response: FullOrganizationUnitResponseInterfaceSingle) => {
          setOrgUnitInfo(response);
        },
        (error: Error) => {
          notification['error']({ message: i18n.translate('api.errorMessage'), duration: 2 });
        }
      );
    }
  }, [userAuth.user]);

  useEffect(() => {
    if (orgUnitInfo?.data?.id) {
      LicenceService.getAll(
        undefined,
        `filter[organizationUnit.id]=${orgUnitInfo?.data?.id}`,
        'sort=id',
        `page[offset]=0&page[limit]=999999`
      ).subscribe(
        (response: LicenceResponseInterfaceAll) => {
          if (response?.data?.length) {
            response?.data?.forEach((licence) => {
              if (
                licence.attributes?.licenceUsers?.find((x) => x.user?.id == userAuth.user?.id) &&
                !licence.relationships?.licenceParent?.data && licence.attributes?.notesNum > 0
              ) {
                setLicenceData(licence);
                const standardLicence = response.data.find(
                  (x) => x.attributes?.licenceUsers?.find((y) => y.user?.id == userAuth.user?.id) && !x.relationships?.licenceParent?.data && x.attributes?.active && x.attributes?.licenceType === LicenceEnum.STANDARD && licence.attributes?.notesNum > 0 && moment().isBefore(moment(licence?.attributes?.to).startOf('day'))
                );
                const premiumLicence = response.data.find(
                  (x) => x.attributes?.licenceUsers?.find((y) => y.user?.id == userAuth.user?.id) && !x.relationships?.licenceParent?.data && x.attributes?.active && x.attributes?.licenceType === LicenceEnum.PREMIUM && licence.attributes?.notesNum > 0 && moment().isBefore(moment(licence?.attributes?.to).startOf('day'))
                );
                if (standardLicence && standardLicence.attributes?.notesNum > 0) setLicenceData(standardLicence);
                if (premiumLicence && premiumLicence.attributes?.notesNum > 0) setLicenceData(premiumLicence);
              }
            });
          }
        },
        (error: Error) => {
          notification['error']({ message: i18n.translate('api.errorMessage'), duration: 2 });
        }
      );
    }
  }, [orgUnitInfo?.data]);

  useEffect(() => {
    clearNoteLinkAction();
    if (selectedLinkId != 0) {
      NoteLinkService.getSingle(selectedLinkId).subscribe(
        (response: NoteLinkResponseInterfaceSingle) => {
          setSelectedLink(response);
        },
        (error: Error) => {
          notification['error']({ message: i18n.translate('api.errorMessage'), duration: 2 });
        }
      );
    }
  }, [selectedLinkId]);

  const onFinishNoteFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  const clearCurrentNote = () => {
    dispatch(clearNoteAction());
  };

  const showConfirm = () => {
    confirm({
      title: i18n.t('notes.confirmDelete'),
      icon: <ExclamationCircleOutlined />,
      content: i18n.t('common.cantUndo'),
      okText: i18n.t('genericButtons.yes'),
      cancelText: i18n.t('genericButtons.cancel'),
      onOk() {
        for (let i = 0; i <= (noteLinkData?.notesLinks?.data?.length as number); i++) {
          noteLinkData.notesLinks?.data.forEach((link) => {
            dispatch(deleteNoteLinkAction(link?.id as number));
          });
        }

        notification['success']({ message: i18n.translate('api.successDelete'), duration: 2 });
        window.scrollTo({ top: 0, behavior: 'smooth' });
        dispatch(deleteNoteAction(parseInt(id)));

        setTimeout(() => {
          NavigationService.navigate(AppRoutes.NOTES.fullPath);
        }, 1000);
      },
    });
  };

  const showConfirmLink = (linkId: number) => {
    confirm({
      title: i18n.t('notes.confirmDeleteLink'),
      icon: <ExclamationCircleOutlined />,
      content: i18n.t('common.cantUndo'),
      okText: i18n.t('genericButtons.yes'),
      cancelText: i18n.t('genericButtons.cancel'),
      onOk() {
        dispatch(deleteNoteLinkAction(linkId));

        window.scrollTo({ top: 0, behavior: 'smooth' });

        setTimeout(() => {
          dispatch(getAllNoteLinksAction(parseInt(id)));
        }, 1000);
      },
    });
  };

  const showConfirmExternal = () => {
    confirm({
      title: i18n.t('notes.confirmDeleteExternal'),
      icon: <ExclamationCircleOutlined />,
      content: i18n.t('common.cantUndo'),
      okText: i18n.t('genericButtons.yes'),
      cancelText: i18n.t('genericButtons.cancel'),
      onOk() {
        let finishNote: any = {
          data: {
            type: 'notes',
            id: noteData.currentNote?.id,
            attributes: {
              title: noteData.currentNote?.attributes?.title,
              text: noteData.currentNote?.attributes?.text,
              type: 'note',
              typeId: null,
              breadcrumb: '--',
              number: noteData.currentNote?.attributes?.number,
              isEditable: noteData.currentNote?.attributes?.isEditable,
              active: noteData.currentNote?.attributes?.active,
              notesObject: null,
            },
            relationships: {},
          },
        };

        dispatch(updateNoteAction(finishNote));
      },
    });
  };

  const loadInitialValues = () => {
    let values: any = {
      title: noteData?.currentNote?.attributes?.title,
      content: noteData?.currentNote?.attributes?.text,
      createdDate: moment(noteData?.currentNote?.attributes?.createdDate),
      external: noteLinkData?.notesLinks?.data[0]?.attributes?.link,
    };

    return values;
  };

  const menu = (
    <Menu
      items={[
        {
          label:
            licenceData?.attributes?.licenceType == LicenceEnum.STANDARD ||
            licenceData?.attributes?.licenceType == LicenceEnum.FREE ? (
              <Popover
                placement="topRight"
                content={i18n.translate(`common.licence.content`)}
                title={i18n.translate(`common.licence.title`)}
              >
                <Button disabled className="cardBtn" type="link">
                  {i18n.translate(`notes.actions.download`)}
                </Button>
              </Popover>
            ) : (
              <Button
                onClick={() => printService.printNoteToPdf(noteData?.currentNote) }
                className="cardBtn"
                type="link"
              >
                {i18n.translate(`notes.actions.download`)}
              </Button>
            ),
          key: '0',
        },
        {
          label: (
            <Button
              danger
              type="link"
              onClick={() => {
                showConfirm();
              }}
            >
              {i18n.translate(`notes.actions.delete`)}
            </Button>
          ),
          key: '1',
        },
      ]}
    />
  );

  const onFinishNote = (values: any) => {
    let finishNote: any = {
      data: {
        type: 'notes',
        id: noteData.currentNote?.id,
        attributes: {
          title: values.title,
          text: values.content,
          type: noteData.currentNote?.attributes?.type,
          typeId: noteData.currentNote?.attributes?.typeId,
          breadcrumb: noteData.currentNote?.attributes?.breadcrumb,
          number: noteData.currentNote?.attributes?.number,
          isEditable: noteData.currentNote?.attributes?.isEditable,
          active: noteData.currentNote?.attributes?.active,
        },
        relationships: {},
      },
    };

    if (isNew && userAuth?.user?.roles !== UserRoleEnum.ADMIN) {
      let userActivityLogData: any = {
        data: {
          type: 'user_activity_log',
          attributes: {
            action: UserActionTypeEnum.NOTE_CREATED,
            extra: finishNote.data?.attributes?.type == 'note' ? undefined : finishNote.data?.attributes?.type
          },
          relationships: {
            user: {
              data: {
                id: userAuth?.user?.id,
                type: 'user'
              },
            },
          },
        },
      };

      UserActivityLogService.create(userActivityLogData);
    }

    isNew ? dispatch(createNoteAction(finishNote)) : dispatch(updateNoteAction(finishNote));

    setTimeout(() => {
      NavigationService.navigate(AppRoutes.NOTES.fullPath + `/${id}`);
    }, 1000);
  };

  return (
    <div className="notesCrud w100-h100">
      {noteData?.currentNote?.attributes ? (
        <>
          <div className="note___breadcrumbs">
            <Breadcrumb>
              <Breadcrumb.Item>
                <CommentOutlined />
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <span>{i18n.translate('breadcrumbs.notes')}</span>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <span>{noteData?.currentNote?.attributes?.title}</span>
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>

          <Form
            layout="vertical"
            name="note"
            size="large"
            scrollToFirstError
            initialValues={noteData.currentNote && !isNew ? loadInitialValues() : { visibility: 'all' }}
            onFinish={onFinishNote}
            onFinishFailed={onFinishNoteFailed}
            autoComplete="off"
            requiredMark={false}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <div className="note__title">{noteData?.currentNote?.attributes?.title}</div>
              </Col>
              <Col>
                <Space>
                  <Dropdown overlay={menu} trigger={['click']}>
                    <Button className="button" size="large" style={{ fontSize: 16, fontWeight: 700, borderRadius: 0 }}>
                      <Space>
                        {i18n.translate(`notes.noteView.actions`)}
                        <DownOutlined />
                      </Space>
                    </Button>
                  </Dropdown>
                  <Link to={AppRoutes.NOTES.fullPath + `/${id}`} key={AppRoutes.NOTES.fullPath + `/${id}`}>
                    <Button
                      onClick={clearCurrentNote}
                      className="button"
                      size="large"
                      style={{ fontSize: 16, fontWeight: 700, borderRadius: 0 }}
                    >
                      {i18n.translate('genericButtons.cancel')}
                    </Button>
                  </Link>
                  <Button
                    className="button"
                    type="primary"
                    size="large"
                    htmlType="submit"
                    style={{ fontSize: 16, fontWeight: 700, borderRadius: 0 }}
                  >
                    {i18n.translate(`notes.noteView.save`)}
                  </Button>
                </Space>
              </Col>
            </Row>

            <Form.Item label={<Text strong>{i18n.translate(`notes.noteView.name`)}</Text>} name="title">
              <Input placeholder={i18n.translate(`notes.noteView.name`)} />
            </Form.Item>

            <Form.Item>
              <Space direction="vertical">
                <Text strong>{i18n.translate(`notes.noteView.linkedContent`)}</Text>
                <Text type="secondary">{noteData?.currentNote?.attributes?.breadcrumb?.toUpperCase()}</Text>
                {noteData?.currentNote?.attributes?.notesObject?.title}
              </Space>
              {noteData?.currentNote?.attributes?.notesObject !== null && (
                <Button
                  className="cardBtn"
                  type="link"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    showConfirmExternal();
                  }}
                />
              )}
            </Form.Item>

            {(noteLinkData?.notesLinks?.data?.length as number) > 0 ? (
              <>
                <Form.Item
                  label={<Text strong>{i18n.translate(`notes.noteView.externalContent`)}</Text>}
                  name="external"
                  rules={[
                    {
                      type: 'url',
                      message: i18n.t(`common.validUrl`),
                    },
                  ]}
                >
                  <Space direction="vertical" size="large">
                    {noteLinkData?.notesLinks?.data?.map((noteLink: NoteLinkInterface) => (
                      <Row align="middle" key={noteLink?.id}>
                        <Col flex={4}>
                          <Button
                            style={{
                              whiteSpace: 'pre-wrap',
                              height: 'auto',
                              textAlign: 'left',
                              overflowWrap: 'anywhere',
                            }}
                            onClick={() => {
                              window.open(noteLink.attributes?.link, '_blank');
                            }}
                          >
                            {noteLink?.attributes?.link}
                          </Button>
                        </Col>

                        <Col flex={0}>
                          <Button
                            className="cardBtn"
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => {
                              setSelectedLinkId(noteLink?.id as number);
                              setisNewLink(false);
                              showModal();
                            }}
                          />
                          <Button
                            className="cardBtn"
                            type="link"
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              showConfirmLink(noteLink.id as number);
                            }}
                          />
                        </Col>
                      </Row>
                    ))}
                    <Form.Item>
                      <Space direction="horizontal" size="large">
                        <Button
                          className="cardBtn"
                          type="link"
                          onClick={() => {
                            setisNewLink(true);
                            showModal();
                          }}
                        >
                          {i18n.translate(`notes.noteView.addLink`)}
                        </Button>
                      </Space>
                    </Form.Item>
                  </Space>
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item label={<Text strong>{i18n.translate(`notes.noteView.externalContent`)}</Text>}>
                  <Space direction="horizontal" size="large">
                    <Button
                      className="cardBtn"
                      type="link"
                      onClick={() => {
                        setisNewLink(true);
                        showModal();
                      }}
                    >
                      {i18n.translate(`notes.noteView.addLink`)}
                    </Button>
                  </Space>
                </Form.Item>
              </>
            )}

            <Form.Item label={<Text strong>{i18n.translate(`notes.noteView.note`)}</Text>} name="content">
              <TextArea showCount placeholder={i18n.translate(`notes.noteView.note`)} rows={25} />
            </Form.Item>
          </Form>

          <NoteLinkModal
            isNew={isNewLink}
            visible={visible}
            handleOk={handleOk}
            handleCancel={handleCancel}
            handleChange={handleChange}
            noteLink={selectedLink?.data as NoteLinkInterface}
            note={noteData?.currentNote as NoteInterface}
          />
        </>
      ) : null}
    </div>
  );
}

export default NotesCrudPageComponent;
